import React, {ReactNode, useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {
    fallbackDownloadUrl,
    formatSize,
    isMobileDevice,
    Platform,
    RustdeskDownload,
    RustdeskState,
    SUPPORT_MAIL,
    SUPPORT_PHONE,
} from "./rustdesk";

/* ---------- Layout ---------- */

// Die Support-Seiten folgen dem System-Theme (hell/dunkel). Die Klasse am <html> färbt
// auch Hintergrund und Footer passend ein und wird beim Verlassen wieder entfernt.
export const SupportLayout = ({children}: { children: ReactNode }) => {
    useEffect(() => {
        const root = document.documentElement;
        root.classList.add("support-theme");
        return () => root.classList.remove("support-theme");
    }, []);

    return <main className="sp">{children}</main>;
};

export const SupportHeading = ({eyebrow, title, lead}: { eyebrow: string; title: string; lead?: ReactNode }) => (
    <header className="sp-heading sp-enter">
        <p className="sp-eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {lead && <p className="sp-lead">{lead}</p>}
    </header>
);

export const ContactLine = () => (
    <p className="sp-contact">
        Fragen? Rufen Sie uns an unter <a href={SUPPORT_PHONE.href}>{SUPPORT_PHONE.display}</a> oder
        schreiben Sie an <a href={`mailto:${SUPPORT_MAIL}`}>{SUPPORT_MAIL}</a>.
    </p>
);

export const BackLink = ({children}: { children: ReactNode }) => (
    <p className="sp-back">
        <Link to="/support">{children}</Link>
    </p>
);

export const Steps = ({children}: { children: ReactNode }) => (
    <section className="sp-card sp-steps-wrap sp-enter sp-enter-2" aria-labelledby="sp-steps-title">
        <h2 id="sp-steps-title">So geht es weiter</h2>
        <ol className="sp-steps">{children}</ol>
    </section>
);

/* ---------- Icons ---------- */

const CheckIcon = () => (
    <svg className="sp-check" viewBox="0 0 52 52" aria-hidden="true">
        <circle className="sp-check-circle" cx="26" cy="26" r="24"/>
        <path className="sp-check-mark" d="M15 27l7.5 7.5L37.5 19"/>
    </svg>
);

const DownloadIcon = () => (
    <svg className="sp-icon" viewBox="0 0 52 52" aria-hidden="true">
        <circle cx="26" cy="26" r="24"/>
        <path d="M26 15v19M18 27l8 8 8-8M17 38h18"/>
    </svg>
);

const WarningIcon = () => (
    <svg className="sp-icon sp-icon-warn" viewBox="0 0 52 52" aria-hidden="true">
        <circle cx="26" cy="26" r="24"/>
        <path d="M26 15v14M26 36v.5"/>
    </svg>
);

/* ---------- Download-Ablauf ---------- */

export type DownloadPhase = "preparing" | "started" | "ready" | "unavailable";

/**
 * Der Download startet erst per Klick auf den Button – ein normaler Link auf die
 * Download-URL (kein <a download>, kein Blob), damit der Windows-Dateiname mit der
 * Serverkonfiguration erhalten bleibt. Solange API-Antwort oder Mac-Variante fehlen,
 * ist der Button deaktiviert; ist die Datei laut API nicht verfügbar, gibt es keinen.
 */
export function useDownloadFlow(api: RustdeskState, platform: Platform | null) {
    const [started, setStarted] = useState(false);
    const [attempt, setAttempt] = useState(0);

    const download: RustdeskDownload | undefined =
        platform && api.status === "ok" ? api.info.downloads[platform] : undefined;
    // Bei API-Ausfall direkt auf den Download-Endpunkt verlinken.
    const url = download?.url ?? (platform ? fallbackDownloadUrl(platform) : undefined);
    const onMobile = isMobileDevice();

    // Für <a href>-Klicks: Die Navigation selbst übernimmt der Browser.
    const markStarted = () => {
        setStarted(true);
        setAttempt(a => a + 1);
    };

    let phase: DownloadPhase;
    if (!platform || api.status === "loading") phase = "preparing";
    else if (api.status === "ok" && !download?.available) phase = "unavailable";
    else phase = started ? "started" : "ready";

    return {phase, download, url, attempt, onMobile, markStarted};
}

type DownloadCardProps = ReturnType<typeof useDownloadFlow> & {
    variantLabel?: string;
};

export const DownloadCard = ({phase, download, url, attempt, onMobile, markStarted, variantLabel}: DownloadCardProps) => {
    const meta = [
        variantLabel,
        download?.version && `Version ${download.version}`,
        formatSize(download?.sizeBytes),
    ].filter(Boolean).join(" · ");

    let icon: ReactNode;
    let title: string;
    let hint: ReactNode = null;

    switch (phase) {
        case "preparing":
            icon = <span className="sp-spinner" aria-hidden="true"/>;
            title = "Einen Moment …";
            break;
        case "started":
            icon = <CheckIcon/>;
            title = "Der Download wurde gestartet";
            hint = <>Sie finden die Datei gleich im Download-Bereich Ihres Browsers. Falls Ihr Browser nachfragt, erlauben Sie den Download.</>;
            break;
        case "unavailable":
            icon = <WarningIcon/>;
            title = "Download gerade nicht verfügbar";
            hint = <>Bitte rufen Sie uns kurz an unter <a href={SUPPORT_PHONE.href}>{SUPPORT_PHONE.display}</a>, wir helfen Ihnen sofort weiter.</>;
            break;
        default:
            icon = <DownloadIcon/>;
            title = "Bereit zum Download";
            if (onMobile) {
                hint = <>Bitte öffnen Sie diese Seite an dem Computer, bei dem wir Ihnen helfen sollen.</>;
            }
    }

    return (
        <section className={`sp-card sp-download sp-enter sp-enter-1 is-${phase}`}>
            <div aria-live="polite">
                <div className="sp-status" key={`${phase}-${attempt}`}>
                    <div className="sp-status-icon">{icon}</div>
                    <div className="sp-status-text">
                        <p className="sp-status-title">{title}</p>
                        {meta && phase !== "unavailable" && <p className="sp-status-meta">{meta}</p>}
                        {hint && <p className="sp-status-hint">{hint}</p>}
                    </div>
                </div>
            </div>

            {phase === "preparing" && (
                <span className="sp-button is-disabled" aria-disabled="true">Jetzt herunterladen</span>
            )}
            {(phase === "ready" || phase === "started") && url && (
                <a className="sp-button" href={url} onClick={markStarted}>
                    {phase === "started" ? "Erneut herunterladen" : "Jetzt herunterladen"}
                </a>
            )}
        </section>
    );
};

/* ---------- Kopierfeld ---------- */

async function copyText(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        // Fallback für ältere Browser / fehlende Berechtigung
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try {
            ok = document.execCommand("copy");
        } catch {
            ok = false;
        }
        document.body.removeChild(ta);
        return ok;
    }
}

export const CopyField = ({label, value}: { label: string; value: string }) => {
    const [copied, setCopied] = useState(false);
    const timer = useRef<number | undefined>(undefined);

    useEffect(() => () => window.clearTimeout(timer.current), []);

    const onCopy = async () => {
        if (!(await copyText(value))) return;
        setCopied(true);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="sp-field">
            <div className="sp-field-body">
                <span className="sp-field-label">{label}</span>
                <code className="sp-field-value">{value}</code>
            </div>
            <button
                type="button"
                className={`sp-copy ${copied ? "is-copied" : ""}`}
                onClick={onCopy}
                aria-label={copied ? `${label} kopiert` : `${label} kopieren`}
            >
                <i className={copied ? "bi bi-check-lg" : "bi bi-copy"} aria-hidden="true"/>
                <span>{copied ? "Kopiert" : "Kopieren"}</span>
            </button>
        </div>
    );
};
